import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Camera, 
  Play, 
  Pause, 
  RotateCcw, 
  Settings, 
  AlertCircle,
  CheckCircle,
  Search,
  Filter,
  Monitor,
  Wifi,
  WifiOff,
  Plus,
  QrCode
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { camerasAPI, sitesAPI } from '../services/api';
import AICameraAnalyzer from '../components/AICameraAnalyzer';
import { useToast } from '../hooks/use-toast';

const Cameras = () => {
  const [cameras, setCameras] = useState([]);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [isLiveView, setIsLiveView] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    siteId: '',
    name: '',
    location: '',
    type: 'fixed',
    resolution: '1080p',
    ipAddress: '',
    rtspUrl: '',
    username: '',  // Admin NVR
    password: '',  // Mot de passe NVR
    port: 554,     // Port RTSP par défaut
    nvr_brand: '', // Hikvision, Dahua, etc.
    nvr_model: '',
    nb_channels: 1,
    nightVision: false,
    motionDetection: false,
    recording: false
  });
  const { toast } = useToast();

  useEffect(() => {
    loadCameras();
    loadSites();
  }, []);

  const loadCameras = async () => {
    try {
      const response = await camerasAPI.getAll();
      setCameras(response.data);
    } catch (error) {
      console.error('Erreur chargement caméras:', error);
      setCameras([]);
    }
  };

  const loadSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      setSites(response.data);
    } catch (error) {
      console.error('Erreur chargement sites:', error);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleAddCamera = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await camerasAPI.create(formData);
      setCameras([...cameras, response.data]);
      setIsAddDialogOpen(false);
      setFormData({
        siteId: '',
        name: '',
        location: '',
        type: 'fixed',
        resolution: '1080p',
        ipAddress: '',
        rtspUrl: '',
        nightVision: false,
        motionDetection: false,
        recording: false
      });
      toast({
        title: "Caméra ajoutée",
        description: "La nouvelle caméra a été configurée avec succès",
      });
      loadCameras();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'ajouter la caméra",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredCameras = cameras.filter(camera => {
    const matchesSearch = camera.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         camera.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSite = selectedSite === 'all' || camera.siteId.toString() === selectedSite;
    return matchesSearch && matchesSite;
  });

  const getStatusColor = (status) => {
    return status === 'En ligne' ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (status) => {
    return status === 'En ligne' ? CheckCircle : AlertCircle;
  };

  return (
    <div className="space-y-6">
      {/* IA Camera analyzer widget */}
      <AICameraAnalyzer />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Caméras de surveillance</h2>
          <p className="text-gray-600">Monitoring en temps réel de vos caméras</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="bg-blue-50 text-blue-700">
            {cameras.filter(c => c.status === 'En ligne').length}/{cameras.length} en ligne
          </Badge>
          <Button onClick={() => setIsAddDialogOpen(true)} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Ajouter caméra</span>
          </Button>
        </div>
      </div>

      {/* Add Camera Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajouter une caméra IP</DialogTitle>
            <DialogDescription>
              Configurez une nouvelle caméra de surveillance pour votre site
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddCamera}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="siteId">Site *</Label>
                  <select
                    id="siteId"
                    name="siteId"
                    value={formData.siteId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de la caméra *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Caméra Entrée Principale"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Emplacement *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Hall d'entrée, Zone A"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="fixed">📹 Caméra Fixe</option>
                    <option value="ptz">🎥 PTZ (Motorisée)</option>
                    <option value="nvr">🖥️ NVR / DVR</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="resolution">Résolution</Label>
                  <select
                    id="resolution"
                    name="resolution"
                    value={formData.resolution}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="720p">720p</option>
                    <option value="1080p">1080p</option>
                    <option value="4K">4K</option>
                  </select>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3 flex items-center">
                  <Wifi className="w-4 h-4 mr-2" />
                  Configuration réseau
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ipAddress">Adresse IP</Label>
                    <Input
                      id="ipAddress"
                      name="ipAddress"
                      value={formData.ipAddress}
                      onChange={handleInputChange}
                      placeholder="192.168.1.100"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="port">Port</Label>
                      <Input
                        id="port"
                        name="port"
                        type="number"
                        value={formData.port}
                        onChange={handleInputChange}
                        placeholder="554"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nvr_brand">Marque NVR</Label>
                      <select
                        id="nvr_brand"
                        name="nvr_brand"
                        value={formData.nvr_brand}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sélectionner</option>
                        <option value="hikvision">Hikvision</option>
                        <option value="dahua">Dahua</option>
                        <option value="axis">Axis</option>
                        <option value="uniview">Uniview</option>
                        <option value="tiandy">Tiandy</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="rtspUrl">URL RTSP / NVR / DVR</Label>
                    <Input
                      id="rtspUrl"
                      name="rtspUrl"
                      value={formData.rtspUrl}
                      onChange={handleInputChange}
                      placeholder="rtsp://192.168.1.100:554/stream"
                    />
                    <p className="text-xs text-gray-500">
                      Ou scannez le QR code de votre NVR/DVR pour configuration automatique
                    </p>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-sm mb-3 flex items-center text-yellow-900">
                      <Settings className="w-4 h-4 mr-2" />
                      Identifiants NVR / Caméra IP
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="username">Nom d'utilisateur</Label>
                        <Input
                          id="username"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          placeholder="admin"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">Mot de passe</Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-yellow-700 mt-2">
                      🔒 Ces identifiants sont nécessaires pour se connecter au NVR/DVR et accéder aux flux vidéo
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Fonctionnalités</h3>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="nightVision"
                      checked={formData.nightVision}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span>Vision nocturne</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="motionDetection"
                      checked={formData.motionDetection}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span>Détection de mouvement</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="recording"
                      checked={formData.recording}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span>Enregistrement actif</span>
                  </label>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Configuration...' : 'Ajouter la caméra'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Rechercher une caméra..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedSite} onValueChange={setSelectedSite}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrer par site" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les sites</SelectItem>
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id.toString()}>
                {site.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={isLiveView ? "default" : "outline"}
          onClick={() => setIsLiveView(!isLiveView)}
        >
          <Monitor className="w-4 h-4 mr-2" />
          Vue live
        </Button>
      </div>

      {isLiveView ? (
        /* Live View Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCameras.map((camera) => (
            <Card key={camera.id} className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">{camera.name}</CardTitle>
                  <div className="flex items-center space-x-2">
                    {camera.status === 'En ligne' ? (
                      <Wifi className="w-4 h-4 text-green-600" />
                    ) : (
                      <WifiOff className="w-4 h-4 text-red-600" />
                    )}
                    <Badge variant={camera.status === 'En ligne' ? 'default' : 'destructive'} className="text-xs">
                      {camera.status}
                    </Badge>
                  </div>
                </div>
                <CardDescription className="text-xs">
                  {camera.siteName} • {camera.location}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-0">
                {/* Live Feed Placeholder */}
                <div className="relative aspect-video bg-gray-900 flex items-center justify-center">
                  {camera.status === 'En ligne' ? (
                    <div className="text-center">
                      <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-2">
                        <Camera className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-white text-sm">Flux en direct</p>
                      <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                        ● LIVE
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <AlertCircle className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">Caméra hors ligne</p>
                    </div>
                  )}
                </div>
                
                {/* Controls */}
                <div className="p-3 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Button size="sm" variant="outline" disabled={camera.status !== 'En ligne'}>
                      <Play className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" disabled={camera.status !== 'En ligne'}>
                      <Pause className="w-3 h-3" />
                    </Button>
                    {camera.type === 'PTZ' && (
                      <Button size="sm" variant="outline" disabled={camera.status !== 'En ligne'}>
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {camera.resolution}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-4">
          {filteredCameras.map((camera) => {
            const StatusIcon = getStatusIcon(camera.status);
            return (
              <Card key={camera.id} className="hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Camera className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{camera.name}</h3>
                        <p className="text-sm text-gray-600">{camera.siteName} • {camera.location}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span>IP: {camera.ipAddress}</span>
                          <span>Type: {camera.type}</span>
                          <span>Résolution: {camera.resolution}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6">
                      {/* Features */}
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <span className={`w-2 h-2 rounded-full ${camera.nightVision ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span>Vision nocturne</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className={`w-2 h-2 rounded-full ${camera.motionDetection ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span>Détection mouvement</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className={`w-2 h-2 rounded-full ${camera.recording ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span>Enregistrement</span>
                        </div>
                      </div>
                      
                      {/* Status */}
                      <div className="flex items-center space-x-2">
                        <StatusIcon className={`w-5 h-5 ${getStatusColor(camera.status)}`} />
                        <div className="text-right">
                          <Badge variant={camera.status === 'En ligne' ? 'default' : 'destructive'}>
                            {camera.status}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            Dernier ping: {camera.lastPing}
                          </p>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setSelectedCamera(camera)}
                          disabled={camera.status !== 'En ligne'}
                        >
                          <Monitor className="w-4 h-4 mr-2" />
                          Voir
                        </Button>
                        <Button size="sm" variant="outline">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Camera Detail Modal */}
      {selectedCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{selectedCamera.name}</CardTitle>
                <Button variant="ghost" onClick={() => setSelectedCamera(null)}>
                  ×
                </Button>
              </div>
              <CardDescription>
                {selectedCamera.siteName} • {selectedCamera.location}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Live Feed */}
                <div className="lg:col-span-2">
                  <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                    <div className="text-center text-white">
                      <Camera className="w-16 h-16 mx-auto mb-4" />
                      <p className="text-lg">Flux en direct</p>
                      <p className="text-sm text-gray-300">{selectedCamera.resolution}</p>
                    </div>
                  </div>
                  
                  {/* Controls */}
                  <div className="flex items-center justify-center space-x-4 mt-4">
                    <Button>
                      <Play className="w-4 h-4 mr-2" />
                      Lecture
                    </Button>
                    <Button variant="outline">
                      <Pause className="w-4 h-4 mr-2" />
                      Pause
                    </Button>
                    <Button variant="outline">
                      Enregistrer
                    </Button>
                    <Button variant="outline">
                      Capture d'écran
                    </Button>
                  </div>
                </div>
                
                {/* Camera Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-3">Informations techniques</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Adresse IP:</span>
                        <span className="font-mono">{selectedCamera.ipAddress}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Type:</span>
                        <span>{selectedCamera.type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Résolution:</span>
                        <span>{selectedCamera.resolution}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Statut:</span>
                        <Badge variant={selectedCamera.status === 'En ligne' ? 'default' : 'destructive'}>
                          {selectedCamera.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3">Fonctionnalités</h3>
                    <div className="space-y-2">
                      {[
                        { label: 'Vision nocturne', enabled: selectedCamera.nightVision },
                        { label: 'Détection de mouvement', enabled: selectedCamera.motionDetection },
                        { label: 'Enregistrement actif', enabled: selectedCamera.recording }
                      ].map((feature, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm">{feature.label}</span>
                          <span className={`w-3 h-3 rounded-full ${feature.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Cameras;